import React, { useEffect, useState } from 'react';
import {
  Box, Button, IconButton, Typography, Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';

/**
 * Floating "Install Wipe" prompt. Handles three cases:
 *   1. Chrome/Edge/Android — listens for `beforeinstallprompt`, caches it,
 *      shows a purple bottom-sheet with an Install button that calls `prompt()`.
 *   2. iOS Safari — no beforeinstallprompt exists, so we fall back to a
 *      "Tap Share → Add to Home Screen" tutorial card.
 *   3. Already installed (standalone) or dismissed — render nothing.
 *
 * Dismissal is persisted in localStorage with a 14-day cooldown so the
 * user isn't nagged after saying "no".
 */

const DISMISS_KEY = 'wipe_install_dismissed_at';
const DISMISS_COOLDOWN_DAYS = 14;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as any).standalone === true
  );
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const when = Number(raw);
    if (!Number.isFinite(when)) return false;
    const ageMs = Date.now() - when;
    return ageMs < DISMISS_COOLDOWN_DAYS * 86400_000;
  } catch {
    return false;
  }
}

function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<'chrome' | 'ios' | null>(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissedRecently()) return;

    // iOS — no beforeinstallprompt, trigger tutorial after a short delay.
    if (isIOS()) {
      const t = setTimeout(() => { setMode('ios'); setVisible(true); }, 8000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setMode('chrome');
      // Stagger the prompt so it doesn't collide with initial load.
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
        setDeferred(null);
      } else {
        handleDismiss();
      }
    } catch {
      handleDismiss();
    }
  };

  if (!visible || !mode) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 'auto' },
          maxWidth: { sm: 380 },
          zIndex: 1400,
          p: 2,
          borderRadius: 3,
          background: (t) => t.palette.background.paper,
          border: '2px solid',
          borderColor: 'text.primary',
          boxShadow: '4px 4px 0 0 currentColor',
          color: 'text.primary',
        }}
      >
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{ position: 'absolute', top: 6, right: 6, color: 'text.disabled' }}
          aria-label="dismiss install prompt"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, pr: 3 }}>
          {mode === 'ios' ? (
            <AddToHomeScreenIcon sx={{ color: 'text.primary', fontSize: 28 }} />
          ) : (
            <InstallDesktopIcon sx={{ color: 'text.primary', fontSize: 28 }} />
          )}
          <Typography
            sx={{
              fontFamily: '"VT323", monospace',
              letterSpacing: 2,
              fontSize: '1.1rem',
              color: 'text.primary',
              fontWeight: 700,
            }}
          >
            INSTALL WIPE
          </Typography>
        </Box>

        {mode === 'chrome' ? (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.4 }}>
              Get the full-screen app experience — no browser chrome, faster launch, home-screen icon.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleInstall}
                sx={{
                  bgcolor: 'text.primary',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'text.primary' },
                }}
              >
                Install
              </Button>
              <Button
                variant="text"
                onClick={handleDismiss}
                sx={{ color: 'text.disabled', textTransform: 'none' }}
              >
                Later
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25, lineHeight: 1.4 }}>
              Add Wipe to your home screen for the full app experience.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'transparent',
                border: '1px dashed',
                mb: 1.25,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.primary' }}>
                Tap <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'middle', color: 'text.primary' }} /> in Safari, then <strong>Add to Home Screen</strong>
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={handleDismiss}
              size="small"
              sx={{ color: 'text.disabled', textTransform: 'none' }}
            >
              Got it
            </Button>
          </>
        )}
      </Box>
    </Slide>
  );
}

export default InstallPrompt;
